/**
 * SkillDelivery Platform — WMS Showcase
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Content authored by Lalit Ratwani, drawing from 22+ years of
 * Manhattan WMS implementation practice (2003-2026).
 *
 * Individual case-study detail view. Sourced from V2 Supabase
 * (case_studies WHERE slug = :slug). Renders only sections that have
 * content — missing sections are hidden rather than shown as "TBD".
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';
import MetaBadge from '../../components/showcase/MetaBadge';
import OutcomesTable from '../../components/showcase/OutcomesTable';
import v2Supabase from '../../lib/v2Supabase';

const BrandGlows = () => (
  <>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-32 -right-32"
      style={{
        width: '500px',
        height: '500px',
        background:
          'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)',
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-24 -left-24"
      style={{
        width: '400px',
        height: '400px',
        background:
          'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)',
      }}
    />
  </>
);

const SectionHeading = ({ children }) => (
  <h2 className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan mt-12 mb-3">
    {children}
  </h2>
);

const Prose = ({ text }) => {
  if (!text) return null;
  return (
    <p className="text-base text-brand-text2 leading-relaxed whitespace-pre-line">
      {text}
    </p>
  );
};

const TechnologyChips = ({ technologies }) => {
  if (!Array.isArray(technologies) || technologies.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {technologies.map((t) => (
        <span
          key={t}
          className="inline-block rounded-brandPill bg-brand-cyan/10 text-brand-cyanDark border border-brand-cyan/30 px-3 py-1 text-xs font-semibold"
        >
          {t}
        </span>
      ))}
    </div>
  );
};

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ok | not-found | error
  const [study, setStudy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await v2Supabase
        .from('case_studies')
        .select('*')
        .eq('slug', slug)
        .maybeSingle()
        .execute();
      if (cancelled) return;
      if (error) {
        // eslint-disable-next-line no-console
        console.error('[CaseStudyDetail] load failed:', error);
        setStatus('error');
        return;
      }
      if (!data) {
        setStatus('not-found');
        return;
      }
      setStudy(data);
      setStatus('ok');
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // --- Loading / error / 404 frames ---------------------------------------
  if (status === 'loading') {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
        <PublicNav />
        <BrandGlows />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 sm:pt-20 pb-16">
          <div className="mt-10 animate-pulse">
            <div className="h-4 w-32 bg-brand-bgOff2 rounded mb-10" />
            <div className="h-10 bg-brand-bgOff2 rounded w-3/4 mb-4" />
            <div className="h-5 bg-brand-bgOff2 rounded w-1/2 mb-8" />
            <div className="space-y-3">
              <div className="h-3 bg-brand-bgOff2 rounded" />
              <div className="h-3 bg-brand-bgOff2 rounded w-5/6" />
              <div className="h-3 bg-brand-bgOff2 rounded w-4/6" />
            </div>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (status === 'not-found' || status === 'error') {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
        <PublicNav />
        <BrandGlows />
        <div className="relative max-w-3xl mx-auto px-6 pt-16 sm:pt-20 pb-16 text-center">
          <div className="mt-16 text-5xl mb-4" aria-hidden="true">
            {status === 'not-found' ? '🔍' : '⚠️'}
          </div>
          <h1 className="text-3xl font-bold text-brand-text">
            {status === 'not-found' ? 'Case study not found' : "Couldn't load case study"}
          </h1>
          <p className="mt-3 text-base text-brand-muted">
            {status === 'not-found'
              ? `We couldn't find a case study with slug "${slug}".`
              : 'Please refresh the page and try again.'}
          </p>
          <div className="mt-8">
            <Link
              to="/domain/wms/case-studies"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand border-2 border-brand-cyan text-brand-cyan font-semibold text-sm bg-white hover:bg-brand-bgOff2 transition-all duration-200"
            >
              ← All case studies
            </Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  // --- Loaded ---------------------------------------------------------------
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
      <PublicNav />
      <BrandGlows />

      <article className="relative max-w-4xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* Back link */}
        <div className="mt-10">
          <Link
            to="/domain/wms/case-studies"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
          >
            ← All case studies
          </Link>
        </div>

        {/* Hero */}
        <header className="mt-8">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Manhattan WMS · Case Study
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tighter leading-tight text-brand-text">
            {study.title}
          </h1>
          {study.subtitle && (
            <p className="mt-3 text-lg text-brand-text2 leading-relaxed">
              {study.subtitle}
            </p>
          )}
          {study.client_descriptor && (
            <p className="mt-4 text-base italic text-brand-muted">
              {study.client_descriptor}
            </p>
          )}

          {/* Meta badges */}
          <div className="mt-5 flex flex-wrap gap-1.5">
            <MetaBadge value={study.industry} label="Industry" variant="cyan" />
            <MetaBadge value={study.manhattan_version} label="Version" variant="blue" />
            <MetaBadge value={study.region} label="Region" variant="muted" />
            <MetaBadge value={study.years} label="Years" variant="muted" />
            <MetaBadge value={study.role} label="Role" variant="muted" />
          </div>
        </header>

        {/* Summary */}
        {study.summary && (
          <>
            <SectionHeading>Summary</SectionHeading>
            <Prose text={study.summary} />
          </>
        )}

        {/* Challenge */}
        {study.challenge && (
          <>
            <SectionHeading>Challenge</SectionHeading>
            <Prose text={study.challenge} />
          </>
        )}

        {/* Solution */}
        {study.solution && (
          <>
            <SectionHeading>Solution</SectionHeading>
            <Prose text={study.solution} />
          </>
        )}

        {/* Outcomes */}
        <SectionHeading>Outcomes</SectionHeading>
        <OutcomesTable outcomes={study.outcomes} />

        {/* Technologies */}
        {Array.isArray(study.technologies) && study.technologies.length > 0 && (
          <>
            <SectionHeading>Technologies</SectionHeading>
            <TechnologyChips technologies={study.technologies} />
          </>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-white border border-brand-border rounded-brandCard p-8">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Got a similar challenge?
          </p>
          <h3 className="mt-3 text-2xl sm:text-3xl font-bold text-brand-text tracking-tight">
            Talk to SkillBot about it.
          </h3>
          <p className="mt-3 text-sm text-brand-muted max-w-xl mx-auto">
            SkillBot for WMS is trained on Manhattan implementation practice —
            ask it about your scenario and see what it returns.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link
              to="/domain/wms"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold text-sm shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200"
            >
              Try SkillBot for WMS →
            </Link>
            <Link
              to="/domain/wms/case-studies"
              className="inline-flex items-center justify-center px-6 py-3 rounded-brand border-2 border-brand-cyan text-brand-cyan font-semibold text-sm bg-white hover:bg-brand-bgOff2 transition-all duration-200"
            >
              ← All case studies
            </Link>
          </div>
        </div>

      </article>

      <PublicFooter />
    </div>
  );
};

export default CaseStudyDetail;
